import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Select, SidePanel, type BadgeVariant } from '@silverfalls/sterling';
import { FilterInput } from '../components/FilterInput.js';
import { useToast } from '../components/Toast.js';
import { formatRelativeDate } from '../lib/format.js';
import { sortBy, toggleSort, type SortState } from '../lib/sort.js';
import {
  fetchRequisitions,
  fetchVendors,
  submitRequisition,
  type RequisitionRow,
  type RequisitionState,
  type SubmitRequisitionInput,
  type SubmitRequisitionLineInput,
  type VendorRow,
} from '../api/p2p.js';

// Sterling density: lead with the descriptive justification (the "why")
// rather than the structural ref. Currency drops at default density (always
// USD across OPCOs); ref drops from the row entirely (URL covers it).
type ReqSortKey =
  | 'justification'
  | 'state'
  | 'lines'
  | 'estimatedTotal'
  | 'createdAt'
  | 'requestingUser';

function reqSortAccessor(req: RequisitionRow, key: ReqSortKey): unknown {
  switch (key) {
    case 'justification':
      return req.justification ?? req.requisitionRef;
    case 'state':
      return req.state;
    case 'lines':
      return req.lines.length;
    case 'estimatedTotal':
      return Number(req.estimatedTotal);
    case 'createdAt':
      return req.createdAt;
    case 'requestingUser':
      return req.requestingUser;
  }
}

function matchesQuery(req: RequisitionRow, q: string): boolean {
  if (q === '') return true;
  const needle = q.toLowerCase();
  return (
    req.requisitionRef.toLowerCase().includes(needle) ||
    req.state.toLowerCase().includes(needle) ||
    (req.justification?.toLowerCase().includes(needle) ?? false) ||
    req.requestingUser.toLowerCase().includes(needle) ||
    (req.suggestedVendor?.legalName.toLowerCase().includes(needle) ?? false) ||
    req.lines.some((l) => l.productName.toLowerCase().includes(needle))
  );
}

const STATE_FILTERS: ReadonlyArray<{ label: string; value: RequisitionState | 'ALL' }> = [
  { label: 'All', value: 'ALL' },
  { label: 'Submitted', value: 'SUBMITTED' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Converted', value: 'CONVERTED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'Withdrawn', value: 'WITHDRAWN' },
];

const REQUESTING_USER = 'u-ui-skeleton';

type DialogState = { open: false } | { open: true };

interface RequisitionsProps {
  readonly opcoRef: string;
}

export function Requisitions({ opcoRef }: RequisitionsProps): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [reqs, setReqs] = useState<ReadonlyArray<RequisitionRow>>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ open: false });
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<RequisitionState | 'ALL'>('ALL');
  const [sort, setSort] = useState<SortState<ReqSortKey> | null>({
    key: 'createdAt',
    direction: 'desc',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchRequisitions(opcoRef);
      setReqs(rows);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [opcoRef]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    let rows = reqs.filter((r) => matchesQuery(r, query));
    if (stateFilter !== 'ALL') {
      rows = rows.filter((r) => r.state === stateFilter);
    }
    return sortBy(rows, sort, reqSortAccessor);
  }, [reqs, query, stateFilter, sort]);

  const onSort = (key: ReqSortKey) => setSort((prev) => toggleSort(prev, key));

  const pendingCount = reqs.filter((r) => r.state === 'SUBMITTED').length;

  const onExportCsv = useCallback(() => {
    exportRequisitionsToCsv(visible, opcoRef);
  }, [visible, opcoRef]);

  return (
    <section className="vendors-screen">
      <div className="screen-header">
        <div>
          <h2>Requisitions</h2>
          <p className="screen-header-desc">
            {reqs.length} {reqs.length === 1 ? 'requisition' : 'requisitions'}
            {pendingCount > 0 && ` · ${pendingCount} awaiting approval`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="secondary"
            icon="download"
            onClick={onExportCsv}
            disabled={visible.length === 0}
            title={
              visible.length === 0
                ? 'No rows to export'
                : `Export ${visible.length} row(s) matching the current filter`
            }
          >
            Export CSV
          </Button>
          <Button
            variant="primary"
            icon="plus"
            onClick={() => setDialog({ open: true })}
          >
            New requisition
          </Button>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-neg" role="alert">
          Couldn't load requisitions: {loadError}
        </div>
      )}

      {loading ? (
        <div className="t-body-sm">Loading…</div>
      ) : reqs.length === 0 ? (
        <div className="empty-state">
          <p>No requisitions in {opcoRef} yet. Submit one to get started.</p>
        </div>
      ) : (
        <>
          <div className="list-toolbar">
            <FilterInput
              value={query}
              onChange={setQuery}
              placeholder="Search by ref, requester, justification, vendor, or item…"
              ariaLabel="Search requisitions"
            />
            <Select
              value={stateFilter}
              onValueChange={(v) => setStateFilter(v as RequisitionState | 'ALL')}
              aria-label="State filter"
              options={STATE_FILTERS.map((f) => ({ value: f.value, label: f.label }))}
            />
            <span className="list-count t-body-sm">
              {visible.length} of {reqs.length}
            </span>
          </div>

          {visible.length === 0 ? (
            <div className="empty-state">
              <p>No requisitions match.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <SortHeader sort={sort} columnKey="justification" onSort={onSort}>
                    Requisition
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="state" onSort={onSort}>
                    State
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="createdAt" onSort={onSort}>
                    Submitted
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="requestingUser" onSort={onSort}>
                    Requester
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="estimatedTotal" onSort={onSort}>
                    Est. total
                  </SortHeader>
                  <SortHeader sort={sort} columnKey="lines" onSort={onSort}>
                    Lines
                  </SortHeader>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr
                    key={r.requisitionRef}
                    className="row-clickable"
                    onClick={() => navigate(`/requisitions/${r.requisitionRef}`)}
                  >
                    <td className="cell-primary">
                      {r.justification || (
                        <span className="mono">{r.requisitionRef.slice(0, 16)}…</span>
                      )}
                    </td>
                    <td>
                      <Badge variant={stateBadgeVariant(r.state)} dot={false}>
                        {r.state}
                      </Badge>
                    </td>
                    <td>{formatRelativeDate(r.createdAt)}</td>
                    <td className="mono">{r.requestingUser}</td>
                    <td className="mono">
                      {r.estimatedTotal} {r.currencyCode}
                    </td>
                    <td>{r.lines.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {dialog.open && (
        <NewRequisitionDialog
          opcoRef={opcoRef}
          onClose={() => setDialog({ open: false })}
          onCreated={(reqRef) => {
            setDialog({ open: false });
            toast.push(`Requisition submitted (${reqRef})`, 'success');
            void load();
          }}
          onError={(message) => toast.push(message, 'error')}
        />
      )}
    </section>
  );
}

function exportRequisitionsToCsv(
  rows: ReadonlyArray<RequisitionRow>,
  opcoRef: string,
): void {
  const header = [
    'requisitionRef',
    'opcoRef',
    'state',
    'requestingUser',
    'justification',
    'currencyCode',
    'estimatedTotal',
    'lineCount',
    'suggestedVendorRef',
    'suggestedVendorLegalName',
    'submittedAt',
    'notes',
  ];
  const escape = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = rows.map((r) =>
    [
      r.requisitionRef,
      r.opcoRef,
      r.state,
      r.requestingUser,
      r.justification ?? '',
      r.currencyCode,
      r.estimatedTotal,
      String(r.lines.length),
      r.suggestedVendorRef ?? '',
      r.suggestedVendor?.legalName ?? '',
      r.createdAt,
      r.notes ?? '',
    ]
      .map(escape)
      .join(','),
  );
  const blob = new Blob([[header.join(','), ...lines].join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const stamp = new Date().toISOString().slice(0, 10);
  a.download = `anvil-requisitions-${opcoRef}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function stateBadgeVariant(state: RequisitionState): BadgeVariant {
  switch (state) {
    case 'APPROVED':
    case 'CONVERTED':
      return 'success';
    case 'SUBMITTED':
      return 'info';
    case 'WITHDRAWN':
      return 'neutral';
    case 'REJECTED':
      return 'warning';
  }
}

interface SortHeaderProps {
  readonly sort: SortState<ReqSortKey> | null;
  readonly columnKey: ReqSortKey;
  readonly onSort: (key: ReqSortKey) => void;
  readonly children: JSX.Element | string;
}

function SortHeader({ sort, columnKey, onSort, children }: SortHeaderProps): JSX.Element {
  const active = sort?.key === columnKey;
  const indicator = active ? (sort?.direction === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th>
      <button
        type="button"
        className={`th-sort${active ? ' th-sort-active' : ''}`}
        onClick={() => onSort(columnKey)}
      >
        {children}
        <span className="th-sort-indicator" aria-hidden>
          {indicator}
        </span>
      </button>
    </th>
  );
}

// ---------------------------------------------------------------------------
// New requisition dialog
// ---------------------------------------------------------------------------

interface NewRequisitionDialogProps {
  readonly opcoRef: string;
  readonly onClose: () => void;
  readonly onCreated: (requisitionRef: string) => void;
  readonly onError: (message: string) => void;
}

interface DraftLine {
  readonly id: string;
  productName: string;
  unitOfMeasure: string;
  quantity: string;
  estimatedUnitPrice: string;
  defaultGLAccount: string;
  suggestedVendorRef: string;
  notes: string;
}

function emptyLine(): DraftLine {
  return {
    id: crypto.randomUUID(),
    productName: '',
    unitOfMeasure: 'EA',
    quantity: '1',
    estimatedUnitPrice: '0',
    defaultGLAccount: '6100-MRO',
    suggestedVendorRef: '',
    notes: '',
  };
}

function NewRequisitionDialog({
  opcoRef,
  onClose,
  onCreated,
  onError,
}: NewRequisitionDialogProps): JSX.Element {
  const [vendors, setVendors] = useState<ReadonlyArray<VendorRow>>([]);
  const [vendorLoadError, setVendorLoadError] = useState<string | null>(null);
  const [currencyCode, setCurrencyCode] = useState<string>('USD');
  const [justification, setJustification] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [lines, setLines] = useState<ReadonlyArray<DraftLine>>([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await fetchVendors(opcoRef);
        if (cancelled) return;
        // All vendors are selectable as suggestion (not just ACTIVE) — a
        // requisition is a demand signal; the approver chooses the actual
        // supplier at conversion time.
        setVendors(rows);
      } catch (err) {
        if (cancelled) return;
        setVendorLoadError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opcoRef]);

  const updateLine = (id: string, patch: Partial<DraftLine>) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (id: string) =>
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((l) => l.id !== id)));

  const subtotal = lines.reduce((sum, l) => {
    const q = Number(l.quantity);
    const p = Number(l.estimatedUnitPrice);
    if (!Number.isFinite(q) || !Number.isFinite(p)) return sum;
    return sum + q * p;
  }, 0);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    const apiLines: SubmitRequisitionLineInput[] = lines.map((l) => {
      const base: SubmitRequisitionLineInput = {
        productName: l.productName.trim(),
        unitOfMeasure: l.unitOfMeasure.trim(),
        quantity: l.quantity.trim(),
        estimatedUnitPrice: l.estimatedUnitPrice.trim(),
        defaultGLAccount: l.defaultGLAccount.trim(),
      };
      const withVendor =
        l.suggestedVendorRef !== ''
          ? { ...base, suggestedVendorRef: l.suggestedVendorRef }
          : base;
      return l.notes.trim() === ''
        ? withVendor
        : { ...withVendor, notes: l.notes.trim() };
    });

    const input: SubmitRequisitionInput = {
      opcoRef,
      currencyCode: currencyCode.trim().toUpperCase(),
      lines: apiLines,
      ...(justification.trim() !== '' ? { justification: justification.trim() } : {}),
      ...(notes.trim() !== '' ? { notes: notes.trim() } : {}),
      requestingUser: REQUESTING_USER,
    };

    try {
      const result = await submitRequisition(input);
      if (result.__typename === 'SubmitRequisitionFailure') {
        const msg = `${result.class}: ${JSON.stringify(result.details)}`;
        setError(msg);
        onError(msg);
        setSubmitting(false);
        return;
      }
      onCreated(result.requisitionRef);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      onError(msg);
      setSubmitting(false);
    }
  };

  return (
    <SidePanel
      open
      title="New requisition"
      width={640}
      onClose={onClose}
      onSave={() => formRef.current?.requestSubmit()}
      dirty={!submitting}
      saveLabel={submitting ? 'Submitting…' : 'Submit requisition'}
    >
      <form ref={formRef} onSubmit={onSubmit}>
          {vendorLoadError && (
            <div className="alert alert-neg" role="alert">
              Couldn't load vendors: {vendorLoadError}
            </div>
          )}

          <div className="form-grid">
            <label className="form-row form-row-full">
              <span className="form-label">Justification</span>
              <input
                type="text"
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder="Why this is needed (e.g. spare parts for line 4)"
              />
            </label>

            <label className="form-row">
              <span className="form-label">Currency (ISO 4217)</span>
              <input
                type="text"
                value={currencyCode}
                onChange={(e) => setCurrencyCode(e.target.value)}
                required
                maxLength={3}
                placeholder="USD"
              />
            </label>

            <label className="form-row form-row-full">
              <span className="form-label">Notes (optional)</span>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes"
              />
            </label>
          </div>

          <div className="po-lines">
            <div className="po-lines-header">
              <span className="form-label">Lines</span>
              <button type="button" className="btn btn-sm" onClick={addLine}>
                + Add line
              </button>
            </div>
            <table className="po-lines-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>UoM</th>
                  <th>Qty</th>
                  <th>Est. price</th>
                  <th>GL</th>
                  <th>Suggested vendor</th>
                  <th>Line total</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => {
                  const q = Number(l.quantity);
                  const p = Number(l.estimatedUnitPrice);
                  const total =
                    Number.isFinite(q) && Number.isFinite(p) ? (q * p).toFixed(4) : '—';
                  return (
                    <tr key={l.id}>
                      <td className="mono">{idx + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={l.productName}
                          onChange={(e) => updateLine(l.id, { productName: e.target.value })}
                          required
                          placeholder="Sprocket"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={l.unitOfMeasure}
                          onChange={(e) => updateLine(l.id, { unitOfMeasure: e.target.value })}
                          required
                          className="po-line-uom"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={l.quantity}
                          onChange={(e) => updateLine(l.id, { quantity: e.target.value })}
                          required
                          className="po-line-num"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={l.estimatedUnitPrice}
                          onChange={(e) =>
                            updateLine(l.id, { estimatedUnitPrice: e.target.value })
                          }
                          required
                          className="po-line-num"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={l.defaultGLAccount}
                          onChange={(e) =>
                            updateLine(l.id, { defaultGLAccount: e.target.value })
                          }
                          required
                          className="po-line-gl"
                        />
                      </td>
                      <td>
                        <select
                          value={l.suggestedVendorRef}
                          onChange={(e) =>
                            updateLine(l.id, { suggestedVendorRef: e.target.value })
                          }
                        >
                          <option value="">— none —</option>
                          {vendors.map((v) => (
                            <option key={v.vendorRef} value={v.vendorRef}>
                              {v.legalName}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="mono">{total}</td>
                      <td>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => removeLine(l.id)}
                          aria-label="Remove line"
                          disabled={lines.length === 1}
                          title={lines.length === 1 ? 'At least one line is required' : 'Remove line'}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={7} className="po-lines-foot-label">
                    Estimated total
                  </td>
                  <td className="mono">{subtotal.toFixed(4)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>

          {error && (
            <div className="alert alert-neg" role="alert">
              {error}
            </div>
          )}

      </form>
    </SidePanel>
  );
}
